import type { Identifier } from "ra-core";
import type {
  LeadAssignmentMethod,
  LeadAssignmentOutcome,
  LeadAssignmentReason,
} from "../types";

/**
 * Almikia Epic EL (TASK-010): pure rules of the "Bitacora de asignacion" screen. Nothing here
 * counts, ranks or compares advisors: the log is a plain chronological list (newest first).
 */

/** One row of the `lead_assignments_summary` view (security_invoker: RLS decides who sees it). */
export type LeadAssignmentLogRow = {
  id: Identifier;
  deal_id: Identifier;
  deal_name?: string | null;
  project_id?: Identifier | null;
  project_name?: string | null;
  channel_code: string;
  channel_label?: string | null;
  method: LeadAssignmentMethod;
  outcome: LeadAssignmentOutcome;
  sales_id?: Identifier | null;
  sales_name?: string | null;
  /** Batch EQ3: the recipient's clave, visible to every caller. */
  advisor_code?: string | null;
  assigned_at: string;
  /** Epic EL2: why the lead went to this advisor (null for rows logged before EL2). */
  reason?: LeadAssignmentReason | null;
  /** Epic EL3: the previous advisor and the written reason of a documented reassignment. */
  previous_sales_id?: Identifier | null;
  reason_text?: string | null;
  /** Batch EQ3: the previous advisor's clave (null on a first assignment). */
  previous_advisor_code?: string | null;
};

/** The three filters of the screen; an empty string means "no filter". */
export type AssignmentLogFilters = {
  projectId: string;
  channelCode: string;
  /** `yyyy-MM-dd`, inclusive, in the America/Mexico_City calendar. */
  dateFrom: string;
  dateTo: string;
};

export const EMPTY_LOG_FILTERS: AssignmentLogFilters = {
  projectId: "",
  channelCode: "",
  dateFrom: "",
  dateTo: "",
};

/** Page size of the log (bounded, paginated query). */
export const LOG_PAGE_SIZE = 25;

/** Mexico has no DST since 2022, so the Mexico City calendar day is a fixed UTC-6 window. */
const MEXICO_CITY_OFFSET = "-06:00";

/** Start of the Mexico City calendar day `yyyy-MM-dd`, as an ISO instant. */
export const startOfMexicoDay = (date: string): string =>
  new Date(`${date}T00:00:00.000${MEXICO_CITY_OFFSET}`).toISOString();

/** Last millisecond of the Mexico City calendar day `yyyy-MM-dd`, as an ISO instant. */
export const endOfMexicoDay = (date: string): string =>
  new Date(`${date}T23:59:59.999${MEXICO_CITY_OFFSET}`).toISOString();

/** Builds the `ra-data-postgrest` filter for the summary view from the screen's filters. */
export const buildLogFilter = (
  filters: AssignmentLogFilters,
): Record<string, string> => ({
  ...(filters.projectId ? { project_id: filters.projectId } : {}),
  ...(filters.channelCode ? { channel_code: filters.channelCode } : {}),
  ...(filters.dateFrom
    ? { "assigned_at@gte": startOfMexicoDay(filters.dateFrom) }
    : {}),
  ...(filters.dateTo
    ? { "assigned_at@lte": endOfMexicoDay(filters.dateTo) }
    : {}),
});

export type AssignmentRowLabels = {
  /** Recipient name, or null when nobody received the lead (shown as "Sin asesora disponible"). */
  recipient: string | null;
  isUnassigned: boolean;
  /** Channel catalogue label, falling back to the raw code. */
  channel: string;
  /** i18n key suffix under `crm.assignmentLog.method`. */
  methodKey: LeadAssignmentMethod;
  /** Epic EL2: i18n key suffix under `resources.guardias.reason`; null for rows logged before EL2. */
  reasonKey: LeadAssignmentReason | null;
};

/** Derives the display labels of one log row (channel, method, recipient/unassigned outcome). */
export const describeAssignment = (
  row: LeadAssignmentLogRow,
): AssignmentRowLabels => {
  const isUnassigned = row.outcome === "no_candidates";
  return {
    recipient: isUnassigned ? null : row.sales_name?.trim() || null,
    isUnassigned,
    channel: row.channel_label?.trim() || row.channel_code,
    methodKey: row.method,
    reasonKey: row.reason ?? null,
  };
};
